
const STATE_KEYS = {
  SUBSCRIBE: "subscribe",
  SELECTED: "selected",
  RESUBSCRIBE: "resubscribe",
};

const DEFAULT_LABELS = {
  [STATE_KEYS.SUBSCRIBE]: "Subscribe",
  [STATE_KEYS.SELECTED]: "Selected",
  [STATE_KEYS.RESUBSCRIBE]: "Resubscribe",
};

function familyClasses(prefix) {
  return {
    variant: `${prefix}ButtonVariant`,
    size: `${prefix}ButtonSize`,
    text: `${prefix}ButtonText`,
    fontColor: `${prefix}ButtonFontColor`,
    backgroundColor: `${prefix}ButtonBackgroundColor`,
    borderColor: `${prefix}ButtonBorderColor`,
    borderWidth: `${prefix}ButtonBorderWidth`,
    icon: `${prefix}ButtonIcon`,
    iconPosition: `${prefix}ButtonIconPosition`,
    iconColor: `${prefix}ButtonIconColor`,
    gap: `${prefix}ButtonIconGap`,
  };
}

const STYLE_FAMILIES = {
  [STATE_KEYS.SUBSCRIBE]: familyClasses("Subscribe"),
  [STATE_KEYS.SELECTED]: familyClasses("Selected"),
  [STATE_KEYS.RESUBSCRIBE]: familyClasses("Resubscribe"),
};

const INHERIT_TOKEN = "__inherit__";

const VARIANTS_WITHOUT_DISABLED_SLOTS = ["text"];

function deriveOptionKey(className) {
  return className.replace(/([a-z])([A-Z])/g, "$1-$2").toLowerCase();
}

function isUnset(value) {
  if (value === undefined || value === null || value === "") {
    return true;
  }
  if (typeof value === "object") {
    return value.source === "inherit" || value.value === INHERIT_TOKEN;
  }
  return value === INHERIT_TOKEN;
}

function pick(deltaValue, baseValue) {
  return isUnset(deltaValue) ? baseValue : deltaValue;
}

function resolveActiveStateKey({ currentSubscription, subscriptionPlanId }) {
  if (!currentSubscription) {
    return STATE_KEYS.SUBSCRIBE;
  }
  const isThisPlan = currentSubscription.subscription_plan_id === subscriptionPlanId;
  const isCancelled = currentSubscription.status === "cancelled";
  if (isThisPlan && !isCancelled) {
    return STATE_KEYS.SELECTED;
  }
  if (isCancelled) {
    return STATE_KEYS.RESUBSCRIBE;
  }
  return STATE_KEYS.SUBSCRIBE;
}

function resolveStatefulButton({ activeState, base, delta = {}, defaultLabels = DEFAULT_LABELS }) {
  const variant = pick(delta.variant, base.variant) || "primary";
  const size = pick(delta.size, base.size) || "standard";
  const backgroundColor = pick(delta.backgroundColor, base.backgroundColor);
  const borderColor = pick(delta.borderColor, base.borderColor);
  const borderWidth = pick(delta.borderWidth, base.borderWidth);
  const gap = pick(delta.gap, base.gap);
  const icon = pick(delta.icon, base.icon);
  const iconPosition = pick(delta.iconPosition, base.iconPosition) || "beforeText";
  const iconColor = pick(delta.iconColor, base.iconColor);
  const text = pick(delta.text, base.text);
  const fontColor = pick(delta.fontColor, base.fontColor);
  const paintsDisabled = activeState === STATE_KEYS.SELECTED && !VARIANTS_WITHOUT_DISABLED_SLOTS.includes(variant);

  const vk = suffix => `${variant}Variant${suffix}`;
  const styleOverrides = {};
  if (!isUnset(borderColor)) {
    styleOverrides[vk("BorderColor")] = borderColor;
    styleOverrides[vk("HoverBorderColor")] = borderColor;
    if (paintsDisabled) {
      styleOverrides[vk("DisabledBorderColor")] = borderColor;
    }
  }
  if (!isUnset(borderWidth)) {
    styleOverrides[vk("BorderWidth")] = borderWidth;
  }
  if (!isUnset(backgroundColor) && backgroundColor !== "none") {
    styleOverrides[vk("BackgroundColor")] = backgroundColor;
    styleOverrides[vk("HoverBackgroundColor")] = backgroundColor;
    if (paintsDisabled) {
      styleOverrides[vk("DisabledBackgroundColor")] = backgroundColor;
    }
  }
  if (!isUnset(fontColor)) {
    styleOverrides[vk("FontColor")] = fontColor;
    styleOverrides[vk("HoverFontColor")] = fontColor;
    if (paintsDisabled) {
      styleOverrides[vk("DisabledFontColor")] = fontColor;
    }
  }
  if (!isUnset(gap)) {
    styleOverrides.contentGap = gap;
  }

  const hasIcon = !isUnset(icon);
  return {
    variant,
    size,
    label: isUnset(text) ? defaultLabels[activeState] ?? "" : text,
    iconLeft: hasIcon && iconPosition === "beforeText" ? icon : "",
    iconRight: hasIcon && (iconPosition === "afterText" || iconPosition === "noText") ? icon : "",
    iconColor: isUnset(iconColor) ? "inherit" : iconColor,
    styleOverrides,
  };
}

module.exports = {
  STATE_KEYS,
  DEFAULT_LABELS,
  STYLE_FAMILIES,
  INHERIT_TOKEN,
  deriveOptionKey,
  isUnset,
  resolveActiveStateKey,
  resolveStatefulButton,
};
